import React, { useState } from "react";
import { Button, Form, Input, Modal, Tag, Upload } from "antd";
import {
  FileTextOutlined,
  TeamOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useCreateDispatchNote } from "~/services/task";
import { useNotification } from "~/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { TASK_KEYS } from "~/services/task/task.keys";
import { TStaff } from "~/services/operation";

interface DispatchNoteModalProps {
  open: boolean;
  onClose: () => void;
  date: string;
  workingStaff: TStaff[];
}

export const DispatchNoteModal: React.FC<DispatchNoteModalProps> = ({
  open,
  onClose,
  date,
  workingStaff,
}) => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<File[]>([]);
  const mutation = useCreateDispatchNote();
  const { success, error } = useNotification();
  const qc = useQueryClient();

  const getStaffName = (staff: TStaff) => {
    if (staff.user) {
      return `${staff.user.firstName || ""} ${staff.user.lastName || ""}`.trim();
    }
    return `${staff.firstName || ""} ${staff.lastName || ""}`.trim() || "Staff";
  };

  const handleSubmit = (values: { title: string; description?: string }) => {
    mutation.mutate(
      {
        title: values.title,
        description: values.description,
        date: dayjs(date).format("YYYY-MM-DD"),
        files: fileList,
        staffIds: workingStaff.map((s) => s.id),
      },
      {
        onSuccess: () => {
          form.resetFields();
          setFileList([]);
          qc.invalidateQueries({
            queryKey: [TASK_KEYS.TASKS],
          });
          success({
            message: "Dispatch note sent",
            description: `${workingStaff.length} staff have been notified.`,
          });
          onClose();
        },
        onError: (err) => {
          console.error("Error creating dispatch note:", err);
          error({
            message: "Error creating dispatch note",
            description: "Please try again.",
          });
        },
      }
    );
  };

  const handleClose = () => {
    form.resetFields();
    setFileList([]);
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      width={600}
      title={null}
      closeIcon={null}
    >
      <div className="flex items-center gap-3 mb-6">
        <span className="flex items-center justify-center w-10 h-10 rounded-full bg-indigo-100 text-indigo-600">
          <FileTextOutlined className="text-lg" />
        </span>
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            Dispatch Note
          </h2>
          <p className="text-sm text-gray-500">
            {dayjs(date).format("dddd, MMMM D, YYYY")} — sent immediately to
            staff working this day
          </p>
        </div>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        size="large"
      >
        <Form.Item
          name="title"
          label="Subject"
          rules={[{ required: true, message: "Subject is required" }]}
        >
          <Input placeholder="e.g. Call times & logistics for today" />
        </Form.Item>

        <Form.Item name="description" label="Details">
          <Input.TextArea
            rows={6}
            maxLength={500}
            showCount
            placeholder="Call times, meeting points, special instructions, extra info for the team..."
          />
        </Form.Item>

        <Form.Item label="Attachments (optional)">
          <Upload
            multiple
            beforeUpload={(file) => {
              setFileList((prev) => [...prev, file as unknown as File]);
              return false;
            }}
            onRemove={(file) => {
              setFileList((prev) =>
                prev.filter((f) => f.name !== file.name)
              );
            }}
            fileList={fileList.map((f, i) => ({
              uid: `${i}`,
              name: f.name,
              status: "done" as const,
            }))}
          >
            <Button icon={<UploadOutlined />}>Attach Files</Button>
          </Upload>
        </Form.Item>

        {/* Staff Recipients */}
        <div className="bg-gray-50 rounded-lg border p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <TeamOutlined className="text-indigo-600" />
            <span className="font-medium text-gray-700">
              Recipients — {workingStaff.length} staff working this day
            </span>
          </div>
          <div className="flex flex-wrap gap-1">
            {workingStaff.map((staff) => (
              <Tag key={staff.id} className="text-xs">
                {getStaffName(staff)}
              </Tag>
            ))}
            {workingStaff.length === 0 && (
              <span className="text-xs text-gray-400">
                No staff assigned to services or tasks on this date yet
              </span>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={mutation.isPending}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            Send Dispatch Note
          </Button>
        </div>
      </Form>
    </Modal>
  );
};
